/*
THIS FILE IS AUTO-GENERATED, NO MANUAL CHANGES IS ALLOWED
*/

import { HttpClient, type RequestParams } from '@atlas/shared/data-access/shared';
import type {
    ChatbotConversationsGetParamsModel,
    ChatbotConversationsInteractionsSummarizePostParamsModel,
    ChatbotConversationsMessagesRatePostParamsModel,
    ChatbotConversationsRatePostParamsModel,
    ChatbotConversationsSummaryGetParamsModel,
    ChatbotIntegrationsGetParamsModel,
    ConversationResponseModel,
    ConversationSummaryResponseModel,
    IntegrationRequestModel,
    RateConversationMessageRequestModel,
    RateConversationRequestModel,
    SendQuestionRequestModel,
    SendQuestionResponseModel,
    SummarizeInteractionRequestModel,
    SummarizeInteractionResponseModel
} from '../models/index';
import {
    ChatbotServiceErrorGuards,
    type ChatbotConversationsGetErrorModel,
    type ChatbotConversationsInteractionsSummarizePostErrorModel,
    type ChatbotConversationsMessagesRatePostErrorModel,
    type ChatbotConversationsPostErrorModel,
    type ChatbotConversationsRatePostErrorModel,
    type ChatbotConversationsSummaryGetErrorModel,
    type ChatbotIntegrationsGetErrorModel,
    type ChatbotIntegrationsPostErrorModel
} from './errors/ChatbotServiceErrors';

export class ChatbotService extends HttpClient {
    /**  ChatbotService API static endpoints */
    static Endpoints = {
        chatbotConversationsGet: ({ id }: any) => `/api/chatbot/conversations/${id}`,

        chatbotConversationsSummaryGet: ({ id }: any) => `/api/chatbot/conversations/${id}/summary`,

        chatbotConversationsPost: '/api/chatbot/conversations',

        chatbotConversationsRatePost: ({ id }: any) => `/api/chatbot/conversations/${id}/rate`,

        chatbotConversationsMessagesRatePost: ({ id, messageId }: any) =>
            `/api/chatbot/conversations/${id}/messages/${messageId}/rate`,

        chatbotConversationsInteractionsSummarizePost: ({ id }: any) =>
            `/api/chatbot/conversations/${id}/interactions/summarize`,

        chatbotIntegrationsPost: '/api/chatbot/integrations',

        chatbotIntegrationsGet: '/api/chatbot/integrations'
    };

    /**  ChatbotService API static errors typeguards */
    static Errors = ChatbotServiceErrorGuards;

    /**  ChatbotService API methods */
    /**
     * @description Gets a current user conversation
     * @tags Chatbot public routes
     * @name ChatbotConversationsGet
     * @summary Gets a conversation
     * @request GET:/api/chatbot/conversations/{id}
     */
    chatbotConversationsGet = (
        { id, ...query }: ChatbotConversationsGetParamsModel,
        requestParams: RequestParams = {}
    ) =>
        this.request<ConversationResponseModel, ChatbotConversationsGetErrorModel>({
            path: ChatbotService.Endpoints.chatbotConversationsGet({ id }),
            method: 'GET',
            format: 'json',
            ...requestParams
        });
    /**
     * @description Gets a current user conversation summary
     * @tags Chatbot public routes
     * @name ChatbotConversationsSummaryGet
     * @summary Gets a conversation summary
     * @request GET:/api/chatbot/conversations/{id}/summary
     */
    chatbotConversationsSummaryGet = (
        { id, ...query }: ChatbotConversationsSummaryGetParamsModel,
        requestParams: RequestParams = {}
    ) =>
        this.request<ConversationSummaryResponseModel, ChatbotConversationsSummaryGetErrorModel>({
            path: ChatbotService.Endpoints.chatbotConversationsSummaryGet({ id }),
            method: 'GET',
            format: 'json',
            ...requestParams
        });
    /**
     * @description Asks a new question on the current or new conversation
     * @tags Chatbot public routes
     * @name ChatbotConversationsPost
     * @summary Starts or continues a conversation
     * @request POST:/api/chatbot/conversations
     */
    chatbotConversationsPost = (data: SendQuestionRequestModel, requestParams: RequestParams = {}) =>
        this.request<SendQuestionResponseModel, ChatbotConversationsPostErrorModel>({
            path: ChatbotService.Endpoints.chatbotConversationsPost,
            method: 'POST',
            body: data,
            type: 'application/json',
            format: 'json',
            ...requestParams
        });
    /**
     * @description Rates an existing conversation
     * @tags Chatbot public routes
     * @name ChatbotConversationsRatePost
     * @summary Rates a conversation
     * @request POST:/api/chatbot/conversations/{id}/rate
     */
    chatbotConversationsRatePost = (
        { id, ...query }: ChatbotConversationsRatePostParamsModel,
        data: RateConversationRequestModel,
        requestParams: RequestParams = {}
    ) =>
        this.request<void, ChatbotConversationsRatePostErrorModel>({
            path: ChatbotService.Endpoints.chatbotConversationsRatePost({ id }),
            method: 'POST',
            body: data,
            type: 'application/json',
            ...requestParams
        });
    /**
     * @description Rates an existing message from within a conversation
     * @tags Chatbot public routes
     * @name ChatbotConversationsMessagesRatePost
     * @summary Rates a message inside a conversation
     * @request POST:/api/chatbot/conversations/{id}/messages/{message_id}/rate
     */
    chatbotConversationsMessagesRatePost = (
        { id, messageId, ...query }: ChatbotConversationsMessagesRatePostParamsModel,
        data: RateConversationMessageRequestModel,
        requestParams: RequestParams = {}
    ) =>
        this.request<void, ChatbotConversationsMessagesRatePostErrorModel>({
            path: ChatbotService.Endpoints.chatbotConversationsMessagesRatePost({
                id,
                messageId
            }),
            method: 'POST',
            body: data,
            type: 'application/json',
            ...requestParams
        });
    /**
     * @description Rates an existing message from within a conversation
     * @tags Chatbot public routes
     * @name ChatbotConversationsInteractionsSummarizePost
     * @summary Summarizes a conversation and wraps with a hermes ticket
     * @request POST:/api/chatbot/conversations/{id}/interactions/summarize
     */
    chatbotConversationsInteractionsSummarizePost = (
        { id, ...query }: ChatbotConversationsInteractionsSummarizePostParamsModel,
        data: SummarizeInteractionRequestModel,
        requestParams: RequestParams = {}
    ) =>
        this.request<SummarizeInteractionResponseModel, ChatbotConversationsInteractionsSummarizePostErrorModel>({
            path: ChatbotService.Endpoints.chatbotConversationsInteractionsSummarizePost({ id }),
            method: 'POST',
            body: data,
            type: 'application/json',
            format: 'json',
            ...requestParams
        });
    /**
     * No description
     * @tags Chatbot private routes
     * @name ChatbotIntegrationsPost
     * @summary Initiates an integration asynchronously.
     * @request POST:/api/chatbot/integrations
     */
    chatbotIntegrationsPost = (data: IntegrationRequestModel, requestParams: RequestParams = {}) =>
        this.request<void, ChatbotIntegrationsPostErrorModel>({
            path: ChatbotService.Endpoints.chatbotIntegrationsPost,
            method: 'POST',
            body: data,
            type: 'application/json',
            ...requestParams
        });
    /**
     * No description
     * @tags Chatbot private routes
     * @name ChatbotIntegrationsGet
     * @summary Checks if the service is accessible
     * @request GET:/api/chatbot/integrations
     */
    chatbotIntegrationsGet = (query?: ChatbotIntegrationsGetParamsModel, requestParams: RequestParams = {}) =>
        this.request<string, ChatbotIntegrationsGetErrorModel>({
            path: ChatbotService.Endpoints.chatbotIntegrationsGet,
            method: 'GET',
            query: query,
            format: 'json',
            ...requestParams
        });
}

export * from './errors/ChatbotServiceErrors';
